import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  Typography
} from "@mui/material";
import { FileText, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import * as Sentry from '@sentry/react'

import professionalProfileApi from "../../../api/professionalProfile";

import UploadModal from "./UploadModal";

import { fileToBase64 } from "../../../api/authService";
import CustomLoader from "../../../components/common/CustomLoader";

const DocumentsTab = ({
  documentFiles,
  onModification,
  onDocumentUpload,
  setNotification,
  dropDownOptions
}) => {
  const [open, setOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [selectedCategoryTab, setSelectedCategoryTab] = useState(0);
  
  const rest = dropDownOptions.documentCategories.filter((cat) => cat.value.toLowerCase() !== 'cv')

  const [loading, setLoading] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleCategoryTabChange = (event, newValue) => {
    setSelectedCategoryTab(newValue);
  };

  const handleDeleteClick = (doc) => {
    setDeleteTarget(doc);
    setDeleteDialogOpen(true);
  };
  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setLoading(true)
    try {
      const res = await professionalProfileApi.deleteDocument(deleteTarget.id);

      if (res.status === 200 || res.status === 204) {
        onDocumentUpload();
        setNotification("Document deleted successfully", "success");
      } else {
        setNotification("Failed to delete document", "error");
      }
    } catch (error) {
      console.error(error);
      setNotification("Error deleting document", "error");
      Sentry.captureException(error, "Document deletion error")
    } finally {
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      setLoading(false)
    }
  };

  const handleSave = async (files) => {
    setLoading(true)
    try {
      let payload = { ...files };
      let transformedPayload = {};
      let finalPayload = {};
      const transformedFiles = await Promise.all(
        payload.files.map(async (file) => {
          if (file instanceof File) {
            return await fileToBase64(file);
          }
          return file;
        })
      );
      finalPayload = { ...payload, files: transformedFiles };

      const res = await professionalProfileApi.createDocuments(finalPayload);

      if (res.status === 200 || res.status === 201) {
        onDocumentUpload();
        setNotification("Documents updated successfully", "success");
      }
      handleClose();

      // // simulate success
      // onModification();
      // setNotification("Documents updated successfully", "success");
    } catch (error) {
      console.error(error);
      setNotification("Error while saving documents", "error");
      Sentry.captureException(error, "Document upload error")
    } finally {
      setLoading(false)
    }
  };

  if (loading) return <CustomLoader />;

  // const handleCreateDocument = async (files) => {
  //   try {
  //     let cvPayload = files.cv;
  //     if (cvPayload) {
  //       if (cvPayload instanceof File) {
  //         cvPayload = await fileToBase64(cvPayload);
  //         const autofillRes = await professionalProfileApi.autoExtractCv({
  //           cv: cvPayload,
  //         });
  //         if (autofillRes.status === 200) {
  //           const { message } = autofillRes.data;
  //           setNotification(message, "success");
  //           onModification();
  //         }
  //       }
  //     }
  //   } catch (error) {
  //     console.error(error);
  //     setNotification(
  //       "Something went wrong while auto populating the fields",
  //       "error"
  //     );
  //   }

  //   try {
  //     let cvPayload = files.cv;
  //     if (cvPayload) {
  //       if (cvPayload instanceof File) {
  //         cvPayload = await fileToBase64(cvPayload);
  //       }
  //     }
  //     const res = await professionalProfileApi.createDocuments({
  //       category: "cv",
  //       files: [cvPayload],
  //     });

  //     if (res.status === 200) {
  //       onModification();
  //       setNotification("CV updated successfully", "success");
  //     }
  //     handleClose();
  //   } catch (error) {
  //     console.error(error);
  //     setNotification("Failed to save document", "error");
  //   }
  //   onDocumentUpload();
  // };

  return (
    <div>
      <>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "center" },
            gap: 2,
            mb: 3,
          }}
        >
          <Typography variant="h6">Supporting Documents</Typography>
          <Button
            startIcon={<Plus size={18} />}
            variant="contained"
            color="primary"
            onClick={handleOpen}
          >
            Upload
          </Button>
        </Box>

        {/* Document cards */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs
            value={selectedCategoryTab}
            onChange={handleCategoryTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              "& .MuiTabs-scrollButtons": {
                width: "auto",
                marginX: 1,
              },
            }}
          >
            {rest.map((doc) => (
              <Tab
                key={doc.value}
                label={doc.label}
                sx={{
                  textTransform: "none",
                  fontSize: "0.95rem",
                  minHeight: "48px",
                }}
              />
            ))}
          </Tabs>
        </Box>

        {/* Documents for Selected Category */}
        <Box>
          {(() => {
            const currentCategory = rest[selectedCategoryTab];
            const categoryDocs = documentFiles.filter(
              (f) => f.category === currentCategory.value
            );

            return categoryDocs.length > 0 ? (
              <ul className="space-y-2">
                {categoryDocs.map((file) => (
                  <li
                    key={file.id}
                    className="flex items-center justify-between bg-blue-50 rounded-md p-3"
                  >
                    <div className="flex items-center gap-2 flex-1">
                      <FileText className="text-blue-500" size={20} />
                      <div className="flex flex-col gap-1 flex-1">
                        <Typography
                          component="a"
                          href={file.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-700 font-medium hover:underline"
                        >
                          {file.file.split("/").pop()}
                        </Typography>
                        <Chip
                          label={`Uploaded at ${new Date(file.uploaded_at).toLocaleDateString()}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ width: 'fit-content' }}
                        />
                      </div>
                    </div>

                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteClick(file)}
                    >
                      <Trash2 size={18} />
                    </IconButton>
                  </li>
                ))}
              </ul>
            ) : (
              <Typography variant="body2" color="textSecondary">
                No documents uploaded for {currentCategory.label}.
              </Typography>
            );
          })()}
        </Box>
      </>

      {/* Dialog for delete confirmation */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{" "}
            <strong>
              {deleteTarget?.file?.split("/").pop() || "this document"}
            </strong>
            ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <UploadModal
        handleClose={handleClose}
        open={open}
        onUpload={handleSave}
        documentCategories={rest}
      />

      {/* <Modal
        handleClose={() => setNewOpen(false)}
        open={newOpen}
        title="Upload Document"
      >
        <DocumentsForm
          onCancel={() => setNewOpen(false)}
          onSubmit={handleCreateDocument}
        />
      </Modal> */}
    </div>
  );
};

export default DocumentsTab;
